/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { AgenciesService } from '@services/requests';
import { useGlobalError } from '@context/ErrorContext';
import { useLayout } from '@layout/app/LayoutContext';
import { useLoading } from '@context/LoadingContext';
import { useManageAgenciesMockStore } from './manage-agencies-mock-store';
import { useDataStateOverride } from '@/shared/ui';
import {
  fiveAgenciesMock,
  emptyAgenciesMock,
  paymentPendingAgencyMock,
  oneActiveMock,
  onePendingMock,
  twoActiveMock,
  twoMixedMock,
  twoPendingMock,
  fiveMixedMock,
} from '@/features/onboarding/data/agency-scenarios';
import type { AgencyDashboardItem } from './ManageAgenciesCards';

const SCENARIO_DATA: Record<string, AgencyDashboardItem[] | null> = {
  api:             null,
  five:            fiveAgenciesMock,
  five_mixed:      fiveMixedMock,
  empty:           emptyAgenciesMock,
  payment_pending: paymentPendingAgencyMock,
  one_active:      oneActiveMock,
  one_pending:     onePendingMock,
  two_active:      twoActiveMock,
  two_mixed:       twoMixedMock,
  two_pending:     twoPendingMock,
};

/**
 * Resolves agency list from the Dev Cockpit mock store (DEBUG=true) or
 * the real API. Always call unconditionally — no Rules of Hooks violations.
 */
export function useAgenciesData(): { agencies: AgencyDashboardItem[]; hasLoaded: boolean; loadError: boolean; retry: () => void } {
  const navigate = useNavigate();
  const { addRequestLoading, removeRequestLoading } = useLoading((s) => s);
  const { updatePopupProps } = useLayout((s) => s);
  const { setGlobalError } = useGlobalError((s) => s);

  // Always call the mock store hook — Rules of Hooks
  const mockScenario = useManageAgenciesMockStore((s) => s.scenario);
  const useMock = mockScenario !== 'api';
  const override = useDataStateOverride();

  const [apiAgencies, setApiAgencies] = useState<AgencyDashboardItem[]>([]);
  const [hasLoaded, setHasLoaded] = useState(useMock);
  const [loadError, setLoadError] = useState(false);

  const handleErrorRef = useRef(setGlobalError);
  handleErrorRef.current = setGlobalError;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const popupRef = useRef(updatePopupProps);
  popupRef.current = updatePopupProps;
  const addRequestLoadingRef = useRef(addRequestLoading);
  addRequestLoadingRef.current = addRequestLoading;
  const removeRequestLoadingRef = useRef(removeRequestLoading);
  removeRequestLoadingRef.current = removeRequestLoading;

  const fetchAgencies = useCallback(() => {
    addRequestLoadingRef.current();
    setLoadError(false);
    AgenciesService.getAllAgencies()
      .then((res) => {
        const items = res.map((a) => ({
          id: a.id,
          name: a.name,
          image: a.image,
          cnpj: a.cnpj,
          postal_code: a.postal_code,
          city: a.city,
          number: a.number,
          complement: a.complement,
          neighborhood: a.neighborhood,
          street: a.street,
          description: a.description,
          state: a.state ?? undefined,
          creation_date: a.creation_date,
          adder: a.adder,
          members: a.members,
          active_subscription_id: (a as Record<string, unknown>).active_subscription_id as string | null ?? null,
          subscription_status: (a as Record<string, unknown>).subscription_status as string | null ?? null,
          payment_status: (a as Record<string, unknown>).payment_status as string | null ?? null,
          plan_id: (a as Record<string, unknown>).plan_id as string | null ?? null,
          status: a.effective_status ?? 'no_subscription',
          last_access: (a as Record<string, unknown>).last_access as string | null ?? null,
        })) as AgencyDashboardItem[];
        setApiAgencies(items);
      })
      .catch((err) => {
        setLoadError(true);
        handleErrorRef.current(err, navigateRef.current, popupRef.current);
      })
      .finally(() => { removeRequestLoadingRef.current(); setHasLoaded(true); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (useMock) {
      setHasLoaded(true);
      return;
    }
    fetchAgencies();
  }, [useMock, fetchAgencies]);

  const agencies = useMock
    ? (SCENARIO_DATA[mockScenario] ?? [])
    : apiAgencies;

  return applyAgenciesOverride(override, agencies, hasLoaded, loadError, fetchAgencies);
}

/**
 * Applies the Dev Cockpit override to the agencies surface. `'loading'` forces
 * `hasLoaded=false`; `'error'` forces `loadError=true` (after the first fetch
 * resolved, so the error branch can render); `'empty'` clears the list and marks
 * loaded; everything else passes through.
 *
 * @param override The forced lifecycle from the Dev Cockpit.
 * @param agencies The resolved agency list.
 * @param hasLoaded Whether the first fetch resolved.
 * @param loadError Whether the last fetch failed.
 * @param retry Re-triggers the agencies fetch.
 * @returns The effective `{ agencies, hasLoaded, loadError, retry }`.
 */
function applyAgenciesOverride(
  override: ReturnType<typeof useDataStateOverride>,
  agencies: AgencyDashboardItem[],
  hasLoaded: boolean,
  loadError: boolean,
  retry: () => void,
): { agencies: AgencyDashboardItem[]; hasLoaded: boolean; loadError: boolean; retry: () => void } {
  if (override === 'loading') return { agencies, hasLoaded: false, loadError: false, retry };
  if (override === 'error') return { agencies, hasLoaded: true, loadError: true, retry };
  if (override === 'empty') return { agencies: [], hasLoaded: true, loadError: false, retry };
  return { agencies, hasLoaded, loadError, retry };
}
